import React, { useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  Box,
  Chip,
  IconButton,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  ArrowForward,
  Add,
  Edit,
  CreditCard,
  Receipt,
  Download,
  MoreVert,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

// Mock data - replace with your actual billingDetails import
const billingDetails = [
  {
    name: "Current Plan",
    desc: "Professional Plan",
    plan: "Upgrade to Premium",
    borderColor: "primary",
    textColor: "primary",
    icon: "💼",
  },
  {
    name: "Monthly Spend",
    desc: "$149.99",
    plan: "View Details",
    borderColor: "success",
    textColor: "success",
    icon: "💰",
  },
  {
    name: "Next Payment",
    desc: "July 15, 2025",
    plan: "Auto-renewal",
    borderColor: "warning",
    textColor: "warning",
    icon: "📅",
  },
];

const paymentMethods = [
  {
    id: 1,
    type: "Visa",
    last4: "1234",
    expiry: "04/2027",
    isDefault: true,
    logo: "https://logos-world.net/wp-content/uploads/2020/04/Visa-Emblem.jpg",
  },
  {
    id: 2,
    type: "Mastercard",
    last4: "5678",
    expiry: "05/2026",
    isDefault: false,
    logo: "https://logos-world.net/wp-content/uploads/2020/09/Mastercard-Symbol.jpg",
  },
  {
    id: 3,
    type: "American Express",
    last4: "9012",
    expiry: "01/2028",
    isDefault: false,
    logo: "https://tse2.mm.bing.net/th?id=OIP.iaYumwI96Gd8b0nAE7m27QHaDA&pid=Api&P=0&h=180",
  },
];

const billingHistory = [
  {
    id: "#INV-2024-001",
    date: "June 15, 2024",
    amount: "$149.99",
    status: "paid",
    description: "Professional Plan - Monthly",
  },
  {
    id: "#INV-2024-002",
    date: "May 15, 2024",
    amount: "$149.99",
    status: "paid",
    description: "Professional Plan - Monthly",
  },
  {
    id: "#INV-2024-003",
    date: "April 15, 2024",
    amount: "$149.99",
    status: "paid",
    description: "Professional Plan - Monthly",
  },
  {
    id: "#INV-2024-004",
    date: "March 15, 2024",
    amount: "$149.99",
    status: "pending",
    description: "Professional Plan - Monthly",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    },
  },
};

const cardHoverVariants = {
  hover: {
    y: -8,
    transition: {
      type: "spring",
      stiffness: 300,
    },
  },
};

const Billing = () => {
  const theme = useTheme();
  const [, setSelectedPayment] = useState(null);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Billing & Payments
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your subscription, payment methods, and billing history
            </Typography>
          </Box>
        </motion.div>

        {/* Billing Overview Cards */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {billingDetails.map((bill, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <motion.div
                  variants={cardHoverVariants}
                  whileHover="hover"
                  whileTap={{ scale: 0.98 }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      borderLeft: `4px solid ${
                        theme.palette[bill.borderColor]?.main
                      }`,
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: "2rem",
                            mr: 2,
                            opacity: 0.8,
                          }}
                        >
                          {bill.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {bill.name}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                              mb: 1,
                            }}
                          >
                            {bill.desc}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        color={bill.borderColor}
                        endIcon={
                          <ArrowForward sx={{ fontSize: "14px !important" }} />
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                        }}
                      >
                        {bill.plan}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Payment Methods */}
        <motion.div variants={itemVariants}>
          <Card sx={{ mb: 4 }}>
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.primary.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CreditCard sx={{ mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Payment Methods
                </Typography>
              </Box>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  Add Payment Method
                </Button>
              </motion.div>
            </Box>
            <Box sx={{ p: 0 }}>
              <AnimatePresence>
                {paymentMethods.map((method, index) => (
                  <motion.div
                    key={method.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedPayment(method.id)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar
                          src={method.logo}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            borderRadius: 1,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {method.type} ending in {method.last4}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Expires {method.expiry}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {method.isDefault && (
                          <Chip
                            label="Default"
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        <IconButton size="small" color="primary" sx={{ ml: 1 }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </AnimatePresence>
            </Box>
          </Card>
        </motion.div>

        {/* Billing History */}
        <motion.div variants={itemVariants}>
          <Card>
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.primary.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Receipt sx={{ mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Billing History
                </Typography>
              </Box>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  Export All
                </Button>
              </motion.div>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {billingHistory.map((transaction, index) => (
                      <motion.tr
                        key={transaction.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        component={TableRow}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {transaction.id}
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>{transaction.date}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {transaction.amount}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status.toUpperCase()}
                            color={getStatusColor(transaction.status)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <IconButton size="small" color="primary">
                                <Download fontSize="small" />
                              </IconButton>
                            </motion.div>
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <IconButton size="small">
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </motion.div>
                          </Box>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </motion.div>
      </motion.div>
    </Container>
  );
};

export default Billing;
